/* ──────────────────────────────────────────────────────────────
   src/hooks/useClientes.ts
   Hook para gestión de clientes usando factory CRUD
   Catálogo de clientes  –  usuarios con rol = 'cliente'
   ────────────────────────────────────────────────────────────── */
import api from '../api/axios';
import { useCrudResource } from './factories/useCrudResource';
import type { Cliente } from '../types/entities';
import type { UsuarioEstadoLog, MotivoDesactivacion } from '../types/usuarios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// Re-exportar tipo para compatibilidad
export type { Cliente };

export function useClientes() {
  return useCrudResource<Cliente, Omit<Cliente, 'id' | 'activo'>>({
    queryKey: ['clientes'],
    endpoint: '/clientes',
    usePatchForUpdate: true, // Clientes usa PATCH en lugar de PUT
    excludeFromCreate: ['activo'], // El backend añade activo=true automáticamente
    transformResponse: (data: any) => {
      // Validación adicional para clientes
      return Array.isArray(data) ? data : [];
    },
  });
}

export function useClientesStats() {
  return useQuery<{ activos: number }, Error>({
    queryKey: ['clientes', 'stats'],
    queryFn: async () => {
      const response = await api.get<{ activos: number }>('/clientes/stats');
      return response.data;
    },
    staleTime: 30000, // 30 segundos
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook para obtener el histórico de cambios de estado de un cliente
 * @param clienteId - ID del cliente
 */
export function useClienteEstadoLog(clienteId: number | null) {
  return useQuery<UsuarioEstadoLog[], Error>({
    queryKey: ['clientes', clienteId, 'estado-log'],
    queryFn: async () => {
      if (!clienteId) return [];
      const response = await api.get<UsuarioEstadoLog[]>(
        `/clientes/${clienteId}/estado-log`,
      );
      return response.data;
    },
    enabled: !!clienteId,
    staleTime: 10000, // 10 segundos
  });
}

/**
 * Hook para actualizar un cliente con motivo de desactivación
 */
export function useUpdateClienteConMotivo() {
  const queryClient = useQueryClient();

  return useMutation<
    Cliente,
    Error,
    { id: number; activo: boolean; deactivationReason?: MotivoDesactivacion }
  >({
    mutationFn: async ({ id, activo, deactivationReason }) => {
      const response = await api.patch<Cliente>(`/clientes/${id}`, {
        activo,
        ...(deactivationReason && { deactivationReason }),
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidar caché de clientes
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
      // Invalidar histórico del cliente específico
      queryClient.invalidateQueries({
        queryKey: ['clientes', variables.id, 'estado-log'],
      });
    },
  });
}
